import { Component, OnInit, AfterViewInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort, Sort } from '@angular/material/sort';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { finalize } from 'rxjs/operators';
import { ConfigStateService } from '@abp/ng.core';
import { CrudService } from 'src/core/services/crud/crud.service';
import { LdapServerDto } from 'src/core/models/settings/ldap-server.dto';
import { ListRequestDto } from 'src/core/models/request/list-request.dto';
import { RoleConstants } from 'src/core/constants/role.constant';

@Component({
  selector: 'ca-ldap-login-settings',
  templateUrl: './ldap-login-settings.component.html',
  styleUrl: './ldap-login-settings.component.scss',
  standalone: false,
})
export class LdapLoginSettingsComponent implements OnInit, AfterViewInit {
  @ViewChild('serverModal') serverModal: TemplateRef<any>;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  dataSource = new MatTableDataSource<LdapServerDto>();
  displayedColumns = ['name', 'serverUrl', 'enabled', 'actions'];
  totalCount = 0;
  pageSize = 10;
  currentPage = 0;

  serverForm: FormGroup;
  isLoading = false;
  isSaving = false;
  canManage = false;

  private modalRef: NgbModalRef;

  constructor(
    private fb: FormBuilder,
    private modalService: NgbModal,
    private crudService: CrudService,
    private configState: ConfigStateService
  ) {}

  ngOnInit(): void {
    const roles: string[] = this.configState.getDeep('currentUser.roles') ?? [];
    this.canManage =
      roles.includes(RoleConstants.admin) || roles.includes(RoleConstants.tenantAdmin);
    this.initForm();
    this.loadServers();
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  loadServers(): void {
    this.isLoading = true;
    const request = new ListRequestDto();
    request.skipCount = 0;
    request.maxResultCount = 1000;
    request.filters = [];
    request.sorters = [];

    this.crudService
      .get<LdapServerDto>(LdapServerDto, request)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(response => {
        const items = response.items ?? [];
        this.dataSource.data = items;
        this.totalCount = response.totalCount ?? items.length;
      });
  }

  private initForm(server?: LdapServerDto): void {
    this.serverForm = this.fb.group({
      id: [server?.id ?? 0],
      name: [server?.name ?? '', { validators: [Validators.required, Validators.maxLength(200)] }],
      enabled: [server?.enabled ?? true],
      ldapOverSsl: [server?.ldapOverSsl ?? false],
      autoVerifyUsers: [server?.autoVerifyUsers ?? true],
      serverHost: [server?.serverHost ?? '', { validators: [Validators.required] }],
      port: [server?.port ?? null, { validators: [Validators.required, Validators.min(1), Validators.max(65535)] }],
      bindUsername: [server?.bindUsername ?? ''],
      password: [server?.password ?? ''],
      ldapDomainName: [server?.ldapDomainName ?? ''],
      skipCertificateValidation: [server?.skipCertificateValidation ?? false],
      certificateName: [server?.certificateName ?? ''],
    });
  }

  openModal(server?: LdapServerDto): void {
    this.initForm(server);
    this.modalRef = this.modalService.open(this.serverModal, {
      size: 'lg',
      backdrop: 'static',
    });
  }

  closeModal(): void {
    this.modalRef?.close();
  }

  getServerUrl(server: LdapServerDto): string {
    const protocol = server.ldapOverSsl ? 'ldaps' : 'ldap';
    return `${protocol}://${server.serverHost}:${server.port}`;
  }

  sortData(event: Sort): void {
    this.dataSource.data = [...this.dataSource.data].sort((a, b) => {
      const isAsc = event.direction === 'asc';
      switch (event.active) {
        case 'name':
          return compare(a.name, b.name, isAsc);
        default:
          return 0;
      }
    });
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex;
  }

  saveServer(): void {
    if (this.serverForm.invalid) {
      return;
    }

    this.isSaving = true;
    const value = this.serverForm.value as LdapServerDto;

    this.crudService
      .save<LdapServerDto>(LdapServerDto, value)
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe(() => {
        this.closeModal();
        this.loadServers();
      });
  }

  deleteServer(): void {
    const id: number = this.serverForm.get('id').value;
    this.isSaving = true;

    this.crudService
      .delete(LdapServerDto, id)
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe(() => {
        this.closeModal();
        this.loadServers();
      });
  }

  get isEditing(): boolean {
    return (this.serverForm?.get('id')?.value ?? 0) > 0;
  }
}

function compare(a: string | number, b: string | number, isAsc: boolean): number {
  return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
}
